import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { SiteTextEditor } from "@/components/SiteTextEditor";
import { BookingInquiries } from "@/components/BookingInquiries";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = ["THOUGHT", "BLOG", "POEM", "COMMENTARY", "ESSAY"];
const THEMES = [
  "TECHNOLOGY", "SOCIETY", "IDENTITY", "BLACKNESS", "TIME", "AMERICA",
  "ATTENTION", "CREATIVITY", "GENERATION", "HUMANITY", "THE FUTURE",
];

const inputCls =
  "w-full bg-transparent border border-blue-100/15 px-4 py-3.5 text-base font-light text-white placeholder:text-blue-100/30 focus:border-[#4F7CFF] focus:outline-none transition-colors duration-300";
const labelCls = "font-meta text-[10px] tracking-[0.3em] text-blue-300/60 block mb-2.5";

export default function Write() {
  const [key, setKey] = useState(() => localStorage.getItem("jw_admin_key") || "");
  const [title, setTitle] = useState("");
  const [type, setType] = useState("THOUGHT");
  const [themes, setThemes] = useState([]);
  const [quote, setQuote] = useState("");
  const [text, setText] = useState("");
  const [dateDisplay, setDateDisplay] = useState("");
  const [sortDate, setSortDate] = useState("");
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [entries, setEntries] = useState([]);
  const [editingSlug, setEditingSlug] = useState(null);
  const [confirmSlug, setConfirmSlug] = useState(null);

  const loadEntries = () =>
    axios
      .get(`${API}/entries`)
      .then((r) => setEntries([...r.data].reverse()))
      .catch(() => {});

  useEffect(() => {
    loadEntries();
  }, []);

  const toggleTheme = (t) =>
    setThemes((p) => (p.includes(t) ? p.filter((x) => x !== t) : [...p, t]));

  const onDateChange = (v) => {
    setDateDisplay(v);
    const m = v.match(/^(\d{2})\.(\d{2})\.(\d{2})$/);
    setSortDate(m ? `20${m[3]}-${m[1]}-${m[2]}` : "");
  };

  const resetForm = () => {
    setTitle("");
    setQuote("");
    setText("");
    setThemes([]);
    setType("THOUGHT");
    setDateDisplay("");
    setSortDate("");
    setEditingSlug(null);
  };

  const publish = async () => {
    setBusy(true);
    setStatus(null);
    const now = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const autoDisplay = `${pad(now.getMonth() + 1)}.${pad(now.getDate())}.${String(now.getFullYear()).slice(2)}`;
    const autoSort = now.toISOString().slice(0, 10);
    const finalSort = sortDate || autoSort;
    const payload = {
      title: title.trim().toUpperCase(),
      date_display: dateDisplay.trim() || autoDisplay,
      sort_date: finalSort,
      year: Number(finalSort.slice(0, 4)),
      type,
      themes,
      text,
      pull_quote: quote.trim() || null,
    };
    try {
      if (editingSlug) {
        await axios.put(`${API}/entries/${editingSlug}`, payload, {
          headers: { "X-Admin-Key": key },
        });
        setStatus({ ok: true, slug: editingSlug, updated: true });
      } else {
        const r = await axios.post(`${API}/entries`, payload, {
          headers: { "X-Admin-Key": key },
        });
        setStatus({ ok: true, slug: r.data.slug });
      }
      localStorage.setItem("jw_admin_key", key);
      resetForm();
      loadEntries();
    } catch (e) {
      setStatus({
        error:
          e.response?.status === 401
            ? "That access key isn't right. Check it and try again."
            : "Something failed. Check your connection and try again.",
      });
    } finally {
      setBusy(false);
    }
  };

  const startEdit = (e) => {
    setEditingSlug(e.slug);
    setTitle(e.title);
    setType(TYPES.includes(e.type) ? e.type : "THOUGHT");
    setThemes(e.themes || []);
    setQuote(e.pull_quote || "");
    setText(e.text);
    setDateDisplay(e.date_display || "");
    setSortDate(e.sort_date || "");
    setStatus(null);
    setConfirmSlug(null);
    window.__lenis?.scrollTo(0, { immediate: true });
    window.scrollTo(0, 0);
  };

  const remove = async (slug) => {
    try {
      await axios.delete(`${API}/entries/${slug}`, { headers: { "X-Admin-Key": key } });
      if (editingSlug === slug) resetForm();
      setConfirmSlug(null);
      loadEntries();
    } catch {
      setStatus({ error: "Delete failed. Check your access key." });
    }
  };

  const ready = key.trim() && title.trim() && text.trim();

  return (
    <main className="relative bg-[#05070F] text-[#F8F8F8] min-h-screen" data-testid="write-page">
      <div className="aura-field" aria-hidden="true" />
      <header className="relative z-10 flex justify-between items-center px-6 sm:px-12 py-5 border-b border-blue-100/10">
        <Link to="/" data-testid="write-back-home" className="font-display font-extrabold text-sm tracking-tight text-aura-gradient">
          JW.
        </Link>
        <span className="font-meta text-[10px] sm:text-xs tracking-[0.25em] text-blue-100/50">
          THE DESK — ONLY JEREMIAH WRITES HERE
        </span>
      </header>

      <div className="relative z-10 max-w-2xl mx-auto px-6 py-14 sm:py-20">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <h1 className="font-display font-extrabold uppercase tracking-tighter text-4xl sm:text-5xl" data-testid="write-heading">
            {editingSlug ? "Edit entry." : "New entry."}
          </h1>
          <p className="text-sm sm:text-base font-light text-blue-100/60 mt-3">
            {editingSlug
              ? "You're editing an existing piece — save to update it on the site."
              : "Write it, publish it, it's on the site — dated today and added to the record."}
          </p>

          {editingSlug && (
            <div className="mt-6 flex items-center justify-between border border-[#4F7CFF] px-4 py-3" data-testid="write-editing-banner">
              <span className="font-meta text-[10px] tracking-[0.25em] text-blue-100/70">
                EDITING: {editingSlug.replace(/-/g, " ").toUpperCase()}
              </span>
              <button
                onClick={resetForm}
                data-testid="write-cancel-edit"
                className="font-meta text-[10px] tracking-[0.25em] text-blue-100/60 hover:text-white transition-colors"
              >
                CANCEL
              </button>
            </div>
          )}

          <div className="mt-10 space-y-7">
            <div>
              <label className={labelCls} htmlFor="write-key">YOUR ACCESS KEY</label>
              <input
                id="write-key"
                type="password"
                value={key}
                onChange={(e) => setKey(e.target.value)}
                placeholder="••••••••••••"
                className={inputCls}
                data-testid="write-key-input"
              />
            </div>

            <div>
              <label className={labelCls} htmlFor="write-title">TITLE</label>
              <input
                id="write-title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="NAME THE THOUGHT"
                className={inputCls}
                data-testid="write-title-input"
              />
            </div>

            <div>
              <label className={labelCls} htmlFor="write-date">DATE (EMPTY = TODAY — CHANGE IT TO MOVE THE PIECE IN THE TIMELINE)</label>
              <input
                id="write-date"
                value={dateDisplay}
                onChange={(e) => onDateChange(e.target.value)}
                placeholder="MM.DD.YY — e.g. 04.23.26"
                className={inputCls}
                data-testid="write-date-input"
              />
            </div>

            <div>
              <span className={labelCls}>TYPE</span>
              <div className="flex flex-wrap gap-2" data-testid="write-type-picker">
                {TYPES.map((t) => (
                  <button
                    key={t}
                    onClick={() => setType(t)}
                    data-testid={`write-type-${t.toLowerCase()}`}
                    className={`font-meta text-[10px] tracking-[0.2em] border px-4 py-2 transition-colors duration-200 ${
                      type === t
                        ? "bg-[#4F7CFF] border-[#4F7CFF] text-white"
                        : "border-blue-100/15 text-blue-100/60 hover:border-blue-100/40"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className={labelCls}>THEMES (OPTIONAL)</span>
              <div className="flex flex-wrap gap-2" data-testid="write-theme-picker">
                {THEMES.map((t) => (
                  <button
                    key={t}
                    onClick={() => toggleTheme(t)}
                    data-testid={`write-theme-${t.toLowerCase().replace(/ /g, "-")}`}
                    className={`font-meta text-[10px] tracking-[0.2em] border px-3 py-1.5 transition-colors duration-200 ${
                      themes.includes(t)
                        ? "bg-white border-white text-black"
                        : "border-blue-100/15 text-blue-100/50 hover:border-blue-100/40"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className={labelCls} htmlFor="write-quote">PULL QUOTE (OPTIONAL)</label>
              <input
                id="write-quote"
                value={quote}
                onChange={(e) => setQuote(e.target.value)}
                placeholder="One line that carries the whole piece"
                className={inputCls}
                data-testid="write-quote-input"
              />
            </div>

            <div>
              <label className={labelCls} htmlFor="write-text">THE WRITING</label>
              <textarea
                id="write-text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Say it exactly how you think it."
                rows={12}
                className={`${inputCls} resize-y leading-relaxed`}
                data-testid="write-text-input"
              />
            </div>

            <button
              onClick={publish}
              disabled={!ready || busy}
              data-testid="write-publish-button"
              className={`w-full font-meta text-xs tracking-[0.3em] py-4 border transition-colors duration-300 ${
                ready && !busy
                  ? "bg-[#4F7CFF] border-[#4F7CFF] text-white hover:bg-white hover:border-white hover:text-black"
                  : "border-blue-100/15 text-blue-100/30 cursor-not-allowed"
              }`}
            >
              {busy ? "SAVING…" : editingSlug ? "SAVE CHANGES" : "PUBLISH TO THE SITE"}
            </button>

            {status?.ok && (
              <div className="border border-[#4F7CFF] p-6 text-center" data-testid="write-success">
                <p className="font-display font-bold uppercase tracking-tight text-lg">
                  {status.updated ? "Changes saved." : "It's live."}
                </p>
                <p className="text-sm font-light text-blue-100/60 mt-2">
                  {status.updated
                    ? "The piece on the site now shows your updated version."
                    : "Your entry is on the site now — in the Journal and the Archive."}
                </p>
                <Link
                  to={`/archive/${status.slug}`}
                  data-testid="write-view-link"
                  className="inline-block mt-4 font-meta text-xs tracking-[0.25em] border border-blue-200/30 rounded-full px-6 py-3 hover:bg-[#4F7CFF] hover:border-[#4F7CFF] transition-colors duration-300"
                >
                  READ IT ON THE SITE →
                </Link>
              </div>
            )}
            {status?.error && (
              <p className="font-meta text-xs tracking-[0.2em] text-red-400 border border-red-400/30 px-4 py-3" data-testid="write-error">
                {status.error}
              </p>
            )}
          </div>

          <BookingInquiries adminKey={key} />

          <div className="mt-16 border-t border-blue-100/10 pt-10">
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="font-display text-2xl font-bold uppercase tracking-tight" data-testid="manager-heading">
                Your entries
              </h2>
              <span className="font-meta text-[10px] tracking-[0.25em] text-blue-300/50">
                {entries.length} TOTAL
              </span>
            </div>
            <div className="border-t border-blue-100/10" data-testid="manager-list">
              {entries.map((e) => (
                <div
                  key={e.slug}
                  className="flex items-center gap-2 sm:gap-3 border-b border-blue-100/10 py-4"
                  data-testid={`manage-entry-${e.slug}`}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-display font-bold uppercase tracking-tight text-sm sm:text-base truncate">
                      {e.title}
                    </p>
                    <p className="font-meta text-[9px] tracking-[0.2em] text-blue-300/50 mt-1">
                      {e.date_display} — {e.type}
                    </p>
                  </div>
                  <Link
                    to={`/archive/${e.slug}`}
                    className="font-meta text-[10px] tracking-[0.2em] text-blue-100/50 hover:text-white px-2 py-1.5 transition-colors"
                    data-testid={`manage-view-${e.slug}`}
                  >
                    VIEW
                  </Link>
                  <button
                    onClick={() => startEdit(e)}
                    className="font-meta text-[10px] tracking-[0.2em] border border-blue-100/20 px-3 py-1.5 hover:bg-[#4F7CFF] hover:border-[#4F7CFF] transition-colors"
                    data-testid={`manage-edit-${e.slug}`}
                  >
                    EDIT
                  </button>
                  {confirmSlug === e.slug ? (
                    <button
                      onClick={() => remove(e.slug)}
                      className="font-meta text-[10px] tracking-[0.2em] border border-red-400/60 text-red-400 px-3 py-1.5"
                      data-testid={`manage-confirm-delete-${e.slug}`}
                    >
                      SURE?
                    </button>
                  ) : (
                    <button
                      onClick={() => setConfirmSlug(e.slug)}
                      className="font-meta text-[10px] tracking-[0.2em] border border-blue-100/20 px-3 py-1.5 hover:border-red-400/60 hover:text-red-400 transition-colors"
                      data-testid={`manage-delete-${e.slug}`}
                    >
                      DELETE
                    </button>
                  )}
                </div>
              ))}
            </div>
            <p className="font-meta text-[9px] tracking-[0.25em] text-blue-200/30 mt-6">
              EDIT LOADS THE PIECE ABOVE. DELETE ASKS TWICE. BOTH NEED YOUR ACCESS KEY.
            </p>
          </div>

          <SiteTextEditor adminKey={key} entries={entries} />
        </motion.div>
      </div>
    </main>
  );
}
